var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useState } from 'react';
import { format, parse } from 'date-fns';
import { ENTER_KEY } from '../../../const/keyNames';
import SwipeableBottomSheet from '../../atoms/SwipeableBottomSheet';
import useWindowSize from '../../customHooks/useWindowWidth';
import Calendar from './Calendar';
import DatePickerTextfield, { dateFormat } from './DatePickerTextfield/index';
import { CalendarIcon, CalenderIconContainer, DatePickerDesktopWrapper, SelectButton } from './subcomponents';
import 'react-datepicker/dist/react-datepicker.css';
// 'shouldCalenderDateBeSet' is to validate if the date is empty/null and  is in range
const shouldCalenderDateBeSet = (date, parsedDate, startDate, endDate) => {
    const isDateAfterStartDate = startDate && parsedDate >= startDate;
    const isDateBeforeEndDate = endDate && parsedDate <= endDate;
    if (date) {
        if (!startDate && !endDate) {
            return true;
        }
        if (isDateAfterStartDate && isDateBeforeEndDate) {
            return true;
        }
        if (isDateAfterStartDate && !endDate) {
            return true;
        }
        if (isDateBeforeEndDate && !startDate) {
            return true;
        }
        return false;
    }
    return false;
};
const DatePicker = (_a) => {
    var { className, startDate, endDate, excludeDates, selectedDate, handleOnChange = () => { }, error, startDateErrorMessage, endDateErrorMessage, excludeDatesErrorMessage, placeholderLabel, idTextField = 'custom-input', isReadOnly, dataAnalytics, dataObject, dataReason, dataType, required, helpText, calendarIcon, calendarDateClickMode = 'select', submitButtonCustomText = 'select date', onBlurHandler } = _a, props = __rest(_a, ["className", "startDate", "endDate", "excludeDates", "selectedDate", "handleOnChange", "error", "startDateErrorMessage", "endDateErrorMessage", "excludeDatesErrorMessage", "placeholderLabel", "idTextField", "isReadOnly", "dataAnalytics", "dataObject", "dataReason", "dataType", "required", "helpText", "calendarIcon", "calendarDateClickMode", "submitButtonCustomText", "onBlurHandler"]);
    const windowWidth = useWindowSize();
    const [calenderDate, setCalenderDate] = useState(selectedDate);
    const [shouldRenderDatePicker, setShouldRenderDatePicker] = useState(false);
    const calenderIconRef = useRef();
    const shouldSubmitOnCalendarDateClicked = calendarDateClickMode === 'submit';
    const isSelectedDateValid = selectedDate && !Number.isNaN(selectedDate.getTime());
    const handleDateTextFieldKeyDown = (date) => {
        // 'parsedDate' converts the date into desired format which is 'MM/dd/yyyy'
        const parsedDate = parse(date, dateFormat, new Date());
        const shouldSetCalenderDate = shouldCalenderDateBeSet(date, parsedDate, startDate, endDate);
        if (shouldSetCalenderDate) {
            setCalenderDate(parsedDate);
        }
        handleOnChange(parsedDate);
    };
    const handleCalendarModalClose = () => {
        // shift focus on the calendar icon
        if (calenderIconRef.current) {
            const calenderIcon = calenderIconRef.current;
            calenderIcon.focus();
        }
        // Close the calendar modal
        setShouldRenderDatePicker(false);
    };
    // handles the calendar date input
    const handleSelectedDateChange = (date) => {
        if (shouldSubmitOnCalendarDateClicked) {
            handleOnChange(date);
            handleCalendarModalClose();
            return;
        }
        setCalenderDate(date);
    };
    const handleDateSubmit = () => {
        handleOnChange(calenderDate);
        handleCalendarModalClose();
    };
    const calendarProps = Object.assign({ className,
        isSelectedDateValid,
        shouldSubmitOnCalendarDateClicked,
        selectedDate,
        calenderDate,
        setCalenderDate,
        setShouldRenderDatePicker,
        excludeDates,
        handleSelectedDateChange,
        startDate,
        endDate,
        required }, props);
    const SubmitDateButton = () => !shouldSubmitOnCalendarDateClicked && (React.createElement(SelectButton, { buttonType: "ghost", onClick: handleDateSubmit }, submitButtonCustomText));
    const commonModalProps = {
        'aria-labelledby': 'datepicker wrapper',
        footerButtons: [React.createElement(SubmitDateButton, { key: "submit button" })],
        id: 'datepicker-wrapper',
        isOpen: shouldRenderDatePicker
    };
    const datePickerTextfieldProps = {
        idTextField,
        onBlurHandler,
        placeholderLabel,
        helpText,
        inputDate: isSelectedDateValid ? format(selectedDate, dateFormat) : '',
        minDate: startDate,
        maxDate: endDate,
        excludeDates,
        startDateErrorMessage,
        endDateErrorMessage,
        excludeDatesErrorMessage,
        error,
        required,
        rightIcon: (React.createElement(CalenderIconContainer, { ref: calenderIconRef, role: "button", tabIndex: 0, "aria-label": "date picker", onClick: () => setShouldRenderDatePicker(true), onKeyDown: (e) => {
                if (e.key === ENTER_KEY) {
                    setShouldRenderDatePicker(true);
                }
            } }, calendarIcon !== null && calendarIcon !== void 0 ? calendarIcon : (React.createElement(CalendarIcon, { size: "2x", icon: error ? ['fal', 'calendar-exclamation'] : ['fas', 'calendar-alt'] })))),
        handleDateTextFieldKeyDown,
        isReadOnly,
        dataObject,
        dataReason,
        dataType,
        dataAnalytics
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DatePickerTextfield, Object.assign({}, datePickerTextfieldProps)),
        !windowWidth.isLessThanTablet && (React.createElement(DatePickerDesktopWrapper, Object.assign({ escapeModalHandler: handleCalendarModalClose, outsideClickHandler: handleCalendarModalClose, footerClassName: "simple-modal-styled-footer" }, commonModalProps),
            React.createElement(Calendar, Object.assign({}, calendarProps)))),
        windowWidth.isLessThanTablet && (React.createElement(SwipeableBottomSheet, Object.assign({ closeModal: handleCalendarModalClose }, commonModalProps),
            React.createElement(Calendar, Object.assign({}, calendarProps))))));
};
export default DatePicker;
